<template>
  <v-card-text>
    <h2>Review Your Details</h2>
    <br />

    <!-- Step 1 -->
    <section>
      <h3>Beneficiary Details</h3>
      <br />
      <p><strong>Name:</strong> {{ form.form1?.beneficiary?.name || "-" }}</p>
      <p>
        <strong>How did you find the job?:</strong>
        {{ form.form1?.jobFoundMethod?.name || "-" }}
      </p>
      <p>
        <strong>Did Odyssea support you?</strong>
        {{
          form.form1?.didOdysseaSupport === true
            ? "Yes"
            : form.form1?.didOdysseaSupport === false
            ? "No"
            : "-"
        }}
      </p>
    </section>

    <v-divider class="my-4" />

    <!-- Step 2 -->
    <section>
      <h3>Current Employment Details</h3>
      <br />
      <p>
        <strong>Part of Network:</strong>
        {{ getPartOfNetworkByValue(form.form2?.partOfNetwork) }}
      </p>

      <!-- If partOfNetwork is YES -->
      <template v-if="form.form2?.partOfNetwork?.value === 'YES'">
        <p>
          <strong>Company:</strong> {{ form.form2?.company?.name || "-" }}
        </p>
        <p>
          <strong>Job Position:</strong> {{ form.form2?.jobTitle?.name || "-" }}
        </p>
      </template>

      <!-- If partOfNetwork is NO or NOT_SURE -->
      <template v-else>
        <p>
          <strong>Employer Name:</strong> {{ form.form2?.employerName || "-" }}
        </p>
        <p>
          <strong>Job Title:</strong> {{ form.form2?.jobTitleText || "-" }}
        </p>
      </template>

      <p>
        <strong>Field of Employment:</strong>
        {{ getCompanySectorById(form.form2?.fieldOfEmployment) }}
      </p>
      <p>
        <strong>Type of Employment:</strong>
        {{ getTypeOfEmploymentByValue(form.form2?.typeOfEmployment) }}
      </p>
      <p>
        <strong>Duration of Contract:</strong>
        {{ getDurationOfContractByValue(form.form2?.durationOfContract) }}
      </p>
      <p>
        <strong>Job Start Date:</strong> {{ form.form2?.jobStartDate || "-" }}
      </p>
      <p><strong>Job End Date:</strong> {{ form.form2?.jobEndDate || "-" }}</p>
      <p>
        <strong>Net Salary Range (€):</strong>
        {{ form.form2?.netSalaryRange?.[0] ?? "-" }} -
        {{ form.form2?.netSalaryRange?.[1] ?? "-" }}
      </p>
      <p>
        <strong>Social Security:</strong>
        {{
          form.form2?.socialSecurity === true
            ? "Yes"
            : form.form2?.socialSecurity === false
            ? "No"
            : "-"
        }}
      </p>
    </section>

    <v-divider class="my-4" />

    <!-- Step 3 -->
    <section>
      <h3>Job Satisfaction</h3>
      <br />
      <p>
        <strong>Job Satisfaction Level:</strong>
        {{ form.form3?.jobSatisfactionLevel ?? "-" }} / 10
      </p>
      <p>
        <strong>Helps Grow Professionally:</strong>
         {{ getHelpsGrowByValue(form.form3?.helpsGrow) }}
      </p>
      <p>
        <strong>Skill Match:</strong> {{ form.form3?.jobMatchSkills ?? "-" }} /
        5
      </p>
      <p>
        <strong>Feel Secure in Job:</strong>
        {{
          form.form3?.feelSecureInJob === true
            ? "Yes"
            : form.form3?.feelSecureInJob === false
            ? "No"
            : "-"
        }}
      </p>
      <p>
        <strong>Team/Manager Relationship:</strong>
        {{ form.form3?.getAlong ?? "-" }} / 5
      </p>
      <p>
        <strong>Recommend Employer:</strong>
        {{ getRecommendEmployerByValue(form.form3?.recommendEmployer) }}
        <p v-if="form.form3?.recommendEmployerReasoning">
          <strong>Reasoning(Optional): </strong>
          {{ form.form3?.recommendEmployerReasoning }}
        </p>
      </p>
    </section>

    <v-divider class="my-4" />

    <!-- Step 4 -->
    <section>
      <h3>Next Job / Priorities</h3>
      <br />
      <p>
        <strong>Still Looking for Job:</strong>
        {{ getStillLookingForJobByValue(form.form4?.stillLookingForJob) }}
      </p>
      <p>
        <strong>Looking For in Next Job:</strong>
        <span v-for="(item, index) in form.form4?.newJobLookingFor" :key="index">
          {{ getNextJobPriorityByValue(item) }}
          <span v-if="index < form.form4?.newJobLookingFor.length - 1">, </span>
        </span>
      </p>
      <p v-if="form.form4?.newJobLookingFor?.includes('OTHER')">
        <strong>Other Priority:</strong>
        {{ form.form4?.otherJobPriority || "-" }}
      </p>
    </section>
  </v-card-text>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useListStore } from "@/stores";

const props = defineProps({
  form: Object,
});

const listStore = useListStore();

// Ensure companiesSectors is always an array to avoid runtime errors
const companiesSectors = computed(() => listStore.companiesSectors || []);

const booleanNotSureOptions = [
  { name: "Yes", value: "YES" },
  { name: "No", value: "NO" },
  { name: "Not sure", value: "NOT_SURE" },
];

const typesOfEmployment = [
  { name: "Full time", value: "FULL_TIME" },
  { name: "Part time", value: "PART_TIME" },
  { name: "Irregular", value: "IRREGULAR" },
  { name: "Freelancer", value: "FREELANCER" },
  { name: "Internship", value: "INTERNSHIP" },
  { name: "No contract (Deprecated)", value: "NO_CONTRACT" },
  { name: "Seasonal (Deprecated)", value: "SEASONAL" },
  { name: "Contract (Deprecated)", value: "CONTRACT" },
  { name: "Other (Deprecated)", value: "OTHER" },
];

const durationsOfContracts = [
  { name: "Indefinite", value: "INDEFINITE" },
  {
    name: "1-month",
    value: "ONE_MONTH",
  },
  { name: "3-month", value: "THREE_MONTH" },
  { name: "6-month", value: "SIX_MONTH" },
  { name: "Other", value: "OTHER" },
];

const nextJobPriorities = [
  { name: "Salary", value: "SALARY" },
  { name: "Location", value: "LOCATION" },
  { name: "Stability", value: "STABILITY" },
  { name: "More suitable role", value: "MORE_SUITABLE_ROLE" },
  { name: "Working hours", value: "WORKING_HOURS" },
  { name: "Employment type", value: "EMPLOYMENT_TYPE" },
  { name: "Other", value: "OTHER" },
];

function getCompanySectorById(id) {
  const element = companiesSectors.value?.find((item) => item.id === id);
  return element ? element.name : "-";
}

function getPartOfNetworkByValue(obj) {
  if(obj == null) return "-"
  const element = booleanNotSureOptions.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getHelpsGrowByValue(obj) {
  if(obj == null) return "-"
  const element = booleanNotSureOptions.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getTypeOfEmploymentByValue(obj) {
  if(obj == null) return "-"
  const element = typesOfEmployment.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getDurationOfContractByValue(obj) {
  if(obj == null) return "-"
  const element = durationsOfContracts.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getRecommendEmployerByValue(obj) {
  if(obj == null) return "-"
  const element = booleanNotSureOptions.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getNextJobPriorityByValue(obj) {
  if(obj == null) return "-"
  const element = nextJobPriorities.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}

function getStillLookingForJobByValue(obj) {
  if(!obj) return "-";
  const element = booleanNotSureOptions.find((item) => item.value === obj.value);
  return element ? element.name : "-";
}


onMounted(async () => {
  await listStore.fetchCompaniesSectors();
});
</script>
